import type Quill from 'quill/core';
import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import type { MutableRefObject } from 'react';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { UiThemeSetting } from '../../graphql/generated/graphql';
import useUiTheme from '../../utils/useUiTheme';
import { useQuill } from '../QuillContext';
import { READONLY_CHANGE_EVENT } from '../constants';
import { EditorActions } from '../editorSlice';
import type { Range } from '../types';
import CodeEmbedEditor from './CodeEmbedEditor';
import type { PreviewRenderer } from './CodeEmbedPreview';
import CodeEmbedPreview from './CodeEmbedPreview';
import CodeEmbedToolbar, { PreviewStatus } from './CodeEmbedToolbar';
import katexRenderToString from './previewRenderers/katexRenderToString';
import getMermaidRenderToString from './previewRenderers/mermaidRenderToString';
import type CodeEmbedFormat from '../formats/CodeEmbed';

export const getPreviewRenderer = (
  language: string,
  darkMode = false,
): PreviewRenderer | null => {
  switch (language) {
    case 'latex':
      return {
        renderToHTML: katexRenderToString,
        allowFullScreen: false,
      };
    case 'mermaid':
      return {
        renderToHTML: getMermaidRenderToString(darkMode),
        allowFullScreen: true,
      };
    default:
      return null;
  }
};

interface CodeEmbedProps {
  blot: CodeEmbedFormat;
  language: string | null;
  syntaxError: Error | null;
  content: Op[];
  blotCommentIds: string[];
  isPreviewing: boolean;
  setIsPreviewing: (isPreviewing: boolean) => void;
  disablePreviewing?: boolean;
  quillRef: MutableRefObject<Quill | null>;
  onContentChange: (content: Delta) => void;
  allowPreviewingAtFirstRender: boolean;
}

const CodeEmbed = ({
  blot,
  language,
  syntaxError,
  content,
  blotCommentIds,
  isPreviewing,
  setIsPreviewing,
  disablePreviewing,
  quillRef,
  onContentChange,
  allowPreviewingAtFirstRender,
}: CodeEmbedProps) => {
  const isFirstRenderRef = useRef(true);
  const { quill } = useQuill();
  const uiTheme = useUiTheme();

  const previewRenderer = useMemo(
    () =>
      language && getPreviewRenderer(language, uiTheme === UiThemeSetting.Dark),
    [language, uiTheme],
  );

  const isContentEmpty = new Delta(content).length() <= 1;

  const shouldPreview = isFirstRenderRef.current
    ? Boolean(
        allowPreviewingAtFirstRender &&
          previewRenderer &&
          !isContentEmpty &&
          !disablePreviewing,
      )
    : isPreviewing;

  useLayoutEffect(() => {
    if (shouldPreview !== isPreviewing) {
      setIsPreviewing(shouldPreview);
    }
  }, [shouldPreview, isPreviewing, setIsPreviewing]);

  useEffect(() => {
    isFirstRenderRef.current = false;
  }, []);

  let status: PreviewStatus = PreviewStatus.NotAvailable;
  if (shouldPreview) {
    status = PreviewStatus.Active;
  } else if (syntaxError) {
    status = PreviewStatus.Errored;
  } else if (previewRenderer) {
    status = PreviewStatus.Available;
  }

  useEffect(() => {
    if (!quill) return undefined;

    const listener = (newReadOnly: boolean) => {
      if (newReadOnly && status === PreviewStatus.Available) {
        setIsPreviewing(true);
      }
    };
    quill.on(READONLY_CHANGE_EVENT, listener);
    return () => {
      quill.off(READONLY_CHANGE_EVENT, listener);
    };
  }, [status, quill, setIsPreviewing]);

  const dispatch = useAppDispatch();
  const handleSelectionChange = useCallback(
    (range: Range | null, yChanged?: boolean) => {
      if (!range || !yChanged) return;
      const index = blot.offset(blot.scroll);
      if (index >= 0) {
        dispatch(
          EditorActions.transmitStateChange({
            type: 'code',
            index,
            code: range,
          }),
        );
      }
    },
    [dispatch, blot],
  );

  return (
    <>
      <CodeEmbedToolbar
        blot={blot}
        status={status}
        setIsPreviewing={setIsPreviewing}
      />
      {shouldPreview && previewRenderer ? (
        <CodeEmbedPreview
          blot={blot}
          content={content}
          blotCommentIds={blotCommentIds}
          setIsPreviewing={setIsPreviewing}
          previewRenderer={previewRenderer}
        />
      ) : (
        <CodeEmbedEditor
          blot={blot}
          initialContent={content}
          language={language}
          syntaxError={isContentEmpty ? null : syntaxError}
          quillRef={quillRef}
          onContentChange={onContentChange}
          onSelectionChange={handleSelectionChange}
        />
      )}
    </>
  );
};

export default CodeEmbed;
